import {
  normalizeWorkerRunDateInputValue,
  renderWorkerInputField,
} from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/Utils/WorkflowConfigurationRunWorker.helpers";
import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import {
  useGetOneWorkerVersion,
  useListWorkers,
  useListWorkerVersions,
} from "#src/components/hooks/adapters/useWorkers";
import { useQueries } from "@tanstack/react-query";
import {
  Button,
  Card,
  DropdownInput,
  EmptyState,
  Form,
  Icon,
  useForm,
} from "@validereinc/common-components";
import {
  AttributeDataType,
  SortDirection,
  WorkerAdapter,
  WorkerAdapterQueryKeys,
  WorkerVersionStatus,
  type InputOutputSchemaPropertyConfigurationType,
  type WorkerRunInputValues,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useWorkflowConfigurationFormStore,
  type RunWorkerFormValuesType,
} from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationRunWorkerDetails.module.scss";

const cx = classNames.bind(styles);

const getDefaultWorkerRunInputValue = (
  definition?: InputOutputSchemaPropertyConfigurationType
): WorkerRunInputValues => {
  if (!definition) {
    return "";
  }

  switch (definition.data_type) {
    case AttributeDataType.BOOLEAN:
      return false;
    case AttributeDataType.MULTI_PICK_LIST:
    case AttributeDataType.MULTI_LOOKUP:
      return [];
    case AttributeDataType.DATE:
    case AttributeDataType.DATE_TIME:
      return null;
    default:
      return "";
  }
};

const isWorkerInputRequired = (
  definition?: InputOutputSchemaPropertyConfigurationType
): boolean => definition?.is_required === true;

/**
 * Normalizes `worker_run_input` so controlled inputs never receive `undefined` and
 * VariableTextInput / Slate always get string values for text fields.
 */
const normalizeWorkerRunInputRecordForForm = (
  raw: Record<string, WorkerRunInputValues>,
  schema: Record<string, InputOutputSchemaPropertyConfigurationType>
): Record<string, WorkerRunInputValues> => {
  const result: Record<string, WorkerRunInputValues> = {};

  for (const [key, rawValue] of Object.entries(raw)) {
    const def = schema[key];

    if (!def) {
      if (rawValue == null) {
        result[key] = "";
        continue;
      }

      if (
        typeof rawValue === "boolean" ||
        typeof rawValue === "number" ||
        typeof rawValue === "string"
      ) {
        result[key] = rawValue;
        continue;
      }

      if (Array.isArray(rawValue)) {
        result[key] = rawValue;
        continue;
      }

      result[key] = String(rawValue);
      continue;
    }

    if (def.data_type === "boolean") {
      if (typeof rawValue === "boolean") {
        result[key] = rawValue;
        continue;
      }

      if (rawValue === "" || rawValue == null) {
        result[key] = false;
        continue;
      }

      if (typeof rawValue === "string") {
        const normalizedString = rawValue.trim().toLowerCase();
        result[key] = normalizedString === "true";
        continue;
      }

      if (typeof rawValue === "number") {
        result[key] = rawValue !== 0;
        continue;
      }

      result[key] = false;
      continue;
    }

    if (
      def.data_type === AttributeDataType.NUMBER ||
      def.data_type === AttributeDataType.INTEGER
    ) {
      if (rawValue === "" || rawValue == null) {
        result[key] = "";
        continue;
      }

      if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
        result[key] =
          def.data_type === AttributeDataType.INTEGER
            ? Math.trunc(rawValue)
            : rawValue;
        continue;
      }

      if (typeof rawValue === "string") {
        const trimmed = rawValue.trim();
        if (trimmed === "") {
          result[key] = "";
          continue;
        }

        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed)) {
          result[key] = rawValue;
          continue;
        }

        result[key] =
          def.data_type === AttributeDataType.INTEGER
            ? Math.trunc(parsed)
            : parsed;
        continue;
      }

      result[key] = String(rawValue);
      continue;
    }

    if (
      def.data_type === AttributeDataType.DATE ||
      def.data_type === AttributeDataType.DATE_TIME
    ) {
      result[key] = normalizeWorkerRunDateInputValue(rawValue, def) ?? null;
      continue;
    }

    if (
      def.data_type === "multi-pick-list" ||
      def.data_type === "multi-lookup"
    ) {
      result[key] = Array.isArray(rawValue) ? rawValue : [];
      continue;
    }

    result[key] =
      rawValue == null
        ? ""
        : typeof rawValue === "string"
          ? rawValue
          : String(rawValue);
  }

  return result;
};

const workerRunInputRecordsDiffer = (
  next: Record<string, WorkerRunInputValues>,
  previous: Record<string, WorkerRunInputValues>
): boolean => {
  const keys = new Set([...Object.keys(next), ...Object.keys(previous)]);
  for (const key of keys) {
    if (next[key] !== previous[key]) return true;
  }
  return false;
};

/** Internal form type that accommodates string version IDs and internal fields */
type RunWorkerFormFields = {
  worker_id?: string;
  worker_version_id?: string;
  worker_run_input: Record<string, WorkerRunInputValues>;
  _add_field_selector?: string | null;
};

type PreviousWorkerSelectionRefsType = {
  previousWorkerIdForVersionAutoSelection?: string;
  previousWorkerIdForInputReset?: string;
  previousVersionIdForInputReset?: string;
};

/**
 * Form component for configuring a run_worker system action in the workflow builder.
 * Follows the same pattern as WorkflowConfigurationRunTemplatedReportDetails:
 * form values are synced to the store via form.getValues(), and the store
 * is the source of truth for initialization via the `values` prop.
 */
const WorkflowConfigurationRunWorkerDetails = ({
  stepId,
}: {
  stepId: string;
}) => {
  const { setRunWorker, setRunWorkerIsValid, getRunWorkerFormValues } =
    useWorkflowConfigurationFormStore();

  const { getAvailableStepVariables } = useWorkflowGraphState();

  const storeValues = getRunWorkerFormValues();
  const latestRunWorkerStoreValuesRef = useRef(storeValues);
  latestRunWorkerStoreValuesRef.current = storeValues;
  const isInitializedRef = useRef(false);

  const form = useForm<RunWorkerFormFields>({
    values: storeValues as unknown as RunWorkerFormFields,
    mode: "onChange",
  });

  const [activeFieldKeys, setActiveFieldKeys] = useState<string[]>([]);

  const selectedWorkerId = form.watch("worker_id");
  const selectedVersionId = form.watch("worker_version_id");
  const availableVariables = getAvailableStepVariables(stepId);
  const removedOptionalFieldKeysRef = useRef<Set<string>>(new Set());
  const previousWorkerSelectionRefs = useRef<PreviousWorkerSelectionRefsType>(
    {}
  );
  const previousStepIdRef = useRef<string | undefined>(undefined);
  const staleStoreValuesWhileStepChangesRef = useRef<
    RunWorkerFormValuesType | undefined
  >(undefined);

  // Reset initialization when stepId changes so the next effect can re-sync from the store.
  // This must run before the init effect below: both fire on step change, and the init effect
  // must see isInitializedRef false (see dependency on stepId there when storeValues is stable).
  useEffect(() => {
    isInitializedRef.current = false;
    if (previousStepIdRef.current !== undefined) {
      staleStoreValuesWhileStepChangesRef.current =
        latestRunWorkerStoreValuesRef.current;
    } else {
      staleStoreValuesWhileStepChangesRef.current = undefined;
    }

    previousStepIdRef.current = stepId;
    setActiveFieldKeys([]);
  }, [stepId]);

  // Sync form and activeFieldKeys from the store when values first arrive, when step changes,
  // or when the store object reference updates.
  useEffect(() => {
    if (storeValues && !isInitializedRef.current) {
      if (
        staleStoreValuesWhileStepChangesRef.current !== undefined &&
        storeValues === staleStoreValuesWhileStepChangesRef.current
      ) {
        return;
      }

      const normalizedRunInput = normalizeWorkerRunInputRecordForForm(
        storeValues.worker_run_input ?? {},
        {}
      );
      form.reset({
        ...(storeValues as unknown as RunWorkerFormFields),
        worker_run_input: normalizedRunInput,
      });

      if (Object.keys(normalizedRunInput).length > 0) {
        setActiveFieldKeys(Object.keys(normalizedRunInput));
      }

      previousWorkerSelectionRefs.current.previousWorkerIdForVersionAutoSelection =
        storeValues.worker_id;
      previousWorkerSelectionRefs.current.previousWorkerIdForInputReset =
        storeValues.worker_id;
      previousWorkerSelectionRefs.current.previousVersionIdForInputReset =
        storeValues.worker_version_id != null
          ? String(storeValues.worker_version_id)
          : undefined;
      staleStoreValuesWhileStepChangesRef.current = undefined;
      isInitializedRef.current = true;
    }
  }, [stepId, storeValues, form]);

  const { data: workersData, isLoading: isLoadingWorkers } = useListWorkers(
    {},
    { enabled: true }
  );
  const allWorkers = workersData?.data ?? [];

  // Check each worker for at least one active version
  const activeVersionChecks = useQueries({
    queries: allWorkers.map((worker) => ({
      queryKey: [
        ...WorkerAdapterQueryKeys.versions.getList({
          meta: { id: worker.id },
          filters: { status: WorkerVersionStatus.ACTIVE },
        }),
        "hasActive",
      ],
      queryFn: () =>
        WorkerAdapter.versions.getList({
          meta: { id: worker.id },
          page: 1,
          pageSize: 1,
          filters: { status: WorkerVersionStatus.ACTIVE },
        }),
      enabled: allWorkers.length > 0,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoadingActiveChecks = activeVersionChecks.some((q) => q.isLoading);

  const workerOptions = useMemo(() => {
    if (isLoadingActiveChecks) {
      return allWorkers.map((w) => ({ label: w.name, value: w.id }));
    }

    return allWorkers
      .filter((_, idx) => {
        const total = activeVersionChecks[idx]?.data?.total_entries ?? 0;
        return total > 0;
      })
      .map((w) => ({ label: w.name, value: w.id }));
  }, [allWorkers, activeVersionChecks, isLoadingActiveChecks]);

  const listVersionsParams = useMemo(
    () =>
      selectedWorkerId
        ? {
            meta: { id: selectedWorkerId },
            page: 1,
            pageSize: 500,
            sortBy: "version" as const,
            sortDirection: SortDirection.DESCENDING,
            filters: { status: WorkerVersionStatus.ACTIVE },
          }
        : null,
    [selectedWorkerId]
  );

  const { data: versionsData, isLoading: isLoadingVersions } =
    useListWorkerVersions(
      listVersionsParams as Parameters<
        typeof WorkerAdapter.versions.getList
      >[0],
      { enabled: Boolean(selectedWorkerId) }
    );

  const activeVersions = versionsData?.data ?? [];
  const latestActiveVersionNumber =
    activeVersions.length > 0
      ? Math.max(...activeVersions.map((v) => v.version))
      : null;

  const versionOptions = useMemo(
    () =>
      activeVersions.map((v) => ({
        label: `v${v.version}${v.version === latestActiveVersionNumber ? " (latest)" : ""}`,
        value: String(v.version),
      })),
    [activeVersions, latestActiveVersionNumber]
  );

  /**
   * Auto-select latest active version. Forces latest when the worker just
   * changed; otherwise only overrides when the current selection is invalid.
   */
  useEffect(() => {
    if (!selectedWorkerId || isLoadingVersions || activeVersions.length === 0)
      return;

    const latestVersion = Math.max(...activeVersions.map((v) => v.version));
    const workerChanged =
      previousWorkerSelectionRefs.current
        .previousWorkerIdForVersionAutoSelection !== selectedWorkerId;

    if (workerChanged) {
      previousWorkerSelectionRefs.current.previousWorkerIdForVersionAutoSelection =
        selectedWorkerId;
      form.setValue("worker_version_id", String(latestVersion));
      return;
    }

    const validVersionNumbers = new Set(activeVersions.map((v) => v.version));
    const currentNumber = selectedVersionId ? Number(selectedVersionId) : NaN;

    if (!Number.isNaN(currentNumber) && validVersionNumbers.has(currentNumber))
      return;

    form.setValue("worker_version_id", String(latestVersion));
  }, [
    selectedWorkerId,
    isLoadingVersions,
    activeVersions,
    selectedVersionId,
    form,
  ]);

  const numericVersionId = selectedVersionId ? Number(selectedVersionId) : 0;

  const workerVersionData = useGetOneWorkerVersion(
    {
      workerId: selectedWorkerId ?? "",
      version: numericVersionId,
    },
    {
      enabled: Boolean(selectedWorkerId) && numericVersionId > 0,
    }
  );

  const inputSchema = useMemo(
    (): Record<string, InputOutputSchemaPropertyConfigurationType> =>
      workerVersionData.data?.input_schema ?? {},
    [workerVersionData.data?.input_schema]
  );

  const inputSchemaEntries = useMemo(
    () =>
      Object.entries(inputSchema).sort(
        ([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0)
      ),
    [inputSchema]
  );

  const requiredInputFieldKeys = useMemo(
    () =>
      inputSchemaEntries
        .filter(([, definition]) => isWorkerInputRequired(definition))
        .map(([key]) => key),
    [inputSchemaEntries]
  );

  /**
   * Clear run-input state when worker or version changes.
   * Runs in `useLayoutEffect` so it executes before other effects: the form→store sync was
   * previously writing stale inputs (e.g. v2's `name`) into the store while
   * `worker_version_id` already pointed at v1, and controlled `values` from the store could
   * repopulate the form.
   */
  useLayoutEffect(() => {
    if (!isInitializedRef.current) return;

    const workerChanged =
      previousWorkerSelectionRefs.current.previousWorkerIdForInputReset !==
      selectedWorkerId;
    const versionChanged =
      previousWorkerSelectionRefs.current.previousVersionIdForInputReset !==
      selectedVersionId;

    if (workerChanged || versionChanged) {
      form.setValue(
        "worker_run_input",
        {} as Record<string, WorkerRunInputValues>
      );
      setActiveFieldKeys([]);
      removedOptionalFieldKeysRef.current.clear();

      if (selectedWorkerId) {
        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          _add_field_selector,
          ...rest
        } = form.getValues();
        setRunWorker({
          ...rest,
          worker_run_input: {},
        } as Parameters<typeof setRunWorker>[0]);
      }
    }

    previousWorkerSelectionRefs.current.previousWorkerIdForInputReset =
      selectedWorkerId;
    previousWorkerSelectionRefs.current.previousVersionIdForInputReset =
      selectedVersionId;
  }, [selectedWorkerId, selectedVersionId, form, setRunWorker]);

  // Filter out active fields that no longer exist in the current schema on version switch
  useEffect(() => {
    if (
      selectedWorkerId &&
      selectedVersionId != null &&
      Object.keys(inputSchema).length > 0
    ) {
      setActiveFieldKeys((prev) => prev.filter((key) => key in inputSchema));
    }
  }, [selectedWorkerId, selectedVersionId, inputSchema]);

  // Required schema fields should always be present and cannot be removed by users.
  useEffect(() => {
    if (
      !selectedWorkerId ||
      selectedVersionId == null ||
      Object.keys(inputSchema).length === 0
    ) {
      return;
    }

    const currentInput = form.getValues("worker_run_input") ?? {};

    setActiveFieldKeys(() => {
      const activeKeysFromSchema = Object.keys(currentInput).filter(
        (key) =>
          key in inputSchema && !removedOptionalFieldKeysRef.current.has(key)
      );
      const keys = new Set([
        ...requiredInputFieldKeys,
        ...activeKeysFromSchema,
      ]);

      return Array.from(keys).sort((firstKey, secondKey) => {
        const firstOrder = inputSchema[firstKey]?.order ?? 0;
        const secondOrder = inputSchema[secondKey]?.order ?? 0;
        return firstOrder - secondOrder;
      });
    });

    const nextInput = { ...currentInput };
    let shouldUpdateInput = false;

    requiredInputFieldKeys.forEach((key) => {
      if (!(key in nextInput)) {
        nextInput[key] = getDefaultWorkerRunInputValue(inputSchema[key]);
        shouldUpdateInput = true;
      }
    });

    const normalizedInput = normalizeWorkerRunInputRecordForForm(
      nextInput,
      inputSchema
    );
    if (
      shouldUpdateInput ||
      workerRunInputRecordsDiffer(normalizedInput, currentInput)
    ) {
      form.setValue("worker_run_input", normalizedInput);
    }
  }, [
    selectedWorkerId,
    selectedVersionId,
    inputSchema,
    requiredInputFieldKeys,
    form,
  ]);

  // Available schema fields not yet added
  const availableFieldsToAdd = useMemo(
    () =>
      inputSchemaEntries
        .filter(([key]) => !activeFieldKeys.includes(key))
        .map(([key, def]) => ({
          label: def.display_name ?? key,
          value: key,
        })),
    [inputSchemaEntries, activeFieldKeys]
  );

  const handleAddField = useCallback(
    (key: string) => {
      removedOptionalFieldKeysRef.current.delete(key);
      setActiveFieldKeys((prev) => [...prev, key]);
      form.setValue(
        `worker_run_input.${key}`,
        getDefaultWorkerRunInputValue(inputSchema[key])
      );
    },
    [form, inputSchema]
  );

  // Watch the add-field selector and add the field when selected
  const fieldToAdd = form.watch("_add_field_selector");
  const isAddingRef = useRef(false);

  useEffect(() => {
    if (fieldToAdd && !isAddingRef.current) {
      isAddingRef.current = true;
      handleAddField(fieldToAdd);
      form.setValue("_add_field_selector", null);
      isAddingRef.current = false;
    }
  }, [fieldToAdd, handleAddField, form]);

  const handleRemoveField = useCallback(
    (key: string) => {
      if (!requiredInputFieldKeys.includes(key)) {
        removedOptionalFieldKeysRef.current.add(key);
      }
      setActiveFieldKeys((prev) => prev.filter((k) => k !== key));

      const currentInput = form.getValues("worker_run_input") ?? {};
      const { [key]: _, ...rest } = currentInput;
      const normalizedRest = normalizeWorkerRunInputRecordForForm(
        rest,
        inputSchema
      );
      form.setValue("worker_run_input", normalizedRest);
    },
    [form, inputSchema, requiredInputFieldKeys]
  );

  // Sync form -> store (matches templated report pattern: form.getValues())
  const watchedRunInput = form.watch("worker_run_input");

  useEffect(() => {
    if (!isInitializedRef.current) return;

    if (selectedWorkerId) {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _add_field_selector,
        ...values
      } = form.getValues();
      setRunWorker({
        ...values,
        worker_run_input: normalizeWorkerRunInputRecordForForm(
          values.worker_run_input ?? {},
          inputSchema
        ),
      } as Parameters<typeof setRunWorker>[0]);
    }
  }, [
    selectedWorkerId,
    selectedVersionId,
    watchedRunInput,
    setRunWorker,
    form,
    inputSchema,
  ]);

  useEffect(() => {
    setRunWorkerIsValid(
      Boolean(selectedWorkerId) &&
        Boolean(selectedVersionId) &&
        form.formState.isValid
    );
  }, [
    selectedWorkerId,
    selectedVersionId,
    setRunWorkerIsValid,
    form.formState.isValid,
  ]);

  const renderInputSchemaField = (key: string) => {
    const def = inputSchema[key];
    if (!def) {
      return null;
    }

    const inputElement = renderWorkerInputField({
      schemaKey: key,
      fieldName: `worker_run_input.${key}`,
      definition: def,
      stringFieldSuggestions: availableVariables,
    });

    return (
      <div
        key={key}
        className={cx("input-field-row")}
      >
        <div className={cx("input-field-row__field")}>{inputElement}</div>
        <Button
          icon="minus-circle"
          variant="error-outline"
          className={cx("input-field-row__remove-button")}
          disabled={isWorkerInputRequired(def)}
          onClick={() => {
            handleRemoveField(key);
          }}
        />
      </div>
    );
  };

  return (
    <Form {...form}>
      <DropdownInput
        name="worker_id"
        label="Worker"
        isRequired
        options={workerOptions}
        labelKey="label"
        valueKey="value"
        isLoading={isLoadingWorkers || isLoadingActiveChecks}
        description="Select the worker to run"
      />

      {selectedWorkerId && (
        <DropdownInput
          name="worker_version_id"
          label="Worker Version"
          isRequired
          options={versionOptions}
          labelKey="label"
          valueKey="value"
          isLoading={isLoadingVersions}
          description="Select the worker version to use"
        />
      )}

      {selectedVersionId != null && (
        <Card className={cx("input-parameters-card")}>
          <div className={cx("input-parameters-card__header")}>
            Input Parameters
          </div>

          {activeFieldKeys.length > 0 ? (
            <div className={cx("input-parameters-card__fields")}>
              {activeFieldKeys.map((key) => renderInputSchemaField(key))}
            </div>
          ) : (
            <EmptyState
              title="No input parameters configured"
              suggestion="Add input parameters using the dropdown below."
              icon={<Icon variant="files" />}
              className={cx("empty-parameters")}
            />
          )}

          {availableFieldsToAdd.length > 0 && (
            <div className={cx("input-parameters-card__add-field")}>
              <DropdownInput
                name="_add_field_selector"
                label="Add Input Parameter"
                placeholder="Select a parameter to add..."
                options={availableFieldsToAdd}
                labelKey="label"
                valueKey="value"
              />
            </div>
          )}

          {inputSchemaEntries.length > 0 &&
            availableFieldsToAdd.length === 0 &&
            activeFieldKeys.length > 0 && (
              <p className={cx("input-parameters-card__all-added-hint")}>
                All available input parameters have been added.
              </p>
            )}
        </Card>
      )}
    </Form>
  );
};

export default WorkflowConfigurationRunWorkerDetails;
